import React from 'react';

/**
 * Action status stepper component for Block Cluster/Wallet operations
 * Displays progress through: VALIDATING → APPROVED → SUBMITTED_ONCHAIN → CONFIRMED_ONCHAIN
 */
const BlockActionStepper = ({
  status,
  txHash,
  error,
  addressCount,
  explorerUrl,
  onClose
}) => {
  const steps = [
    { key: 'VALIDATING', label: 'Validating', icon: 'bi-shield-check' },
    { key: 'APPROVED', label: 'Approved', icon: 'bi-check-circle' },
    { key: 'SUBMITTED_ONCHAIN', label: 'Submitted', icon: 'bi-broadcast' },
    { key: 'CONFIRMED_ONCHAIN', label: 'Confirmed', icon: 'bi-patch-check-fill' },
  ];

  const getStepStatus = (stepKey) => {
    if (status === 'FAILED') {
      const stepIndex = steps.findIndex(s => s.key === stepKey);
      const failedAtIndex = steps.findIndex(s => s.key === status);
      return stepIndex <= failedAtIndex ? 'failed' : 'pending';
    }

    const currentIndex = steps.findIndex(s => s.key === status);
    const stepIndex = steps.findIndex(s => s.key === stepKey);

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  const isFailed = status === 'FAILED';
  const isComplete = status === 'CONFIRMED_ONCHAIN';

  return (
    <div className="bg-dark border border-secondary rounded p-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 text-white">
          <i className={`bi ${isComplete ? 'bi-shield-fill-check text-success' : isFailed ? 'bi-shield-fill-x text-danger' : 'bi-shield-fill text-warning'} me-2`}></i>
          {isComplete ? 'Blocking Complete' : isFailed ? 'Blocking Failed' : 'Blocking in Progress'}
        </h6>
        {(isComplete || isFailed) && onClose && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
            title="Dismiss"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>

      {/* Stepper */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        {steps.map((step, idx) => {
          const stepStatus = getStepStatus(step.key);

          return (
            <React.Fragment key={step.key}>
              {/* Step */}
              <div className="d-flex flex-column align-items-center">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center mb-1 ${stepStatus === 'completed' ? 'bg-success' :
                    stepStatus === 'current' ? 'bg-warning' :
                      stepStatus === 'failed' ? 'bg-danger' :
                        'bg-secondary'
                    }`}
                  style={{ width: 32, height: 32 }}
                >
                  {stepStatus === 'current' && !isFailed ? (
                    <div className="spinner-border spinner-border-sm text-dark" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : stepStatus === 'completed' ? (
                    <i className="bi bi-check text-white"></i>
                  ) : stepStatus === 'failed' ? (
                    <i className="bi bi-x text-white"></i>
                  ) : (
                    <i className={`bi ${step.icon} text-dark`}></i>
                  )}
                </div>
                <span className={`small ${stepStatus === 'completed' ? 'text-success' :
                  stepStatus === 'current' ? 'text-warning' :
                    stepStatus === 'failed' ? 'text-danger' :
                      'text-secondary'
                  }`}>
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {idx < steps.length - 1 && (
                <div
                  className={`flex-grow-1 mx-2 ${getStepStatus(steps[idx + 1].key) !== 'pending' ? 'bg-success' : 'bg-secondary'
                    }`}
                  style={{ height: 2, marginBottom: 20 }}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Status Message */}
      <div className="bg-secondary-subtle rounded p-2 small">
        {isFailed && error && (
          <div className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {isComplete && (
          <div className="text-success">
            <i className="bi bi-check-circle me-2"></i>
            Successfully blocked {addressCount || 1} wallet{addressCount > 1 ? 's' : ''} on-chain
          </div>
        )}

        {!isFailed && !isComplete && (
          <div className="text-warning">
            <i className="bi bi-hourglass-split me-2"></i>
            {status === 'VALIDATING' && 'Simulating EigenLayer AVS validation...'}
            {status === 'APPROVED' && 'Fetching wallet addresses...'}
            {status === 'SUBMITTED_ONCHAIN' && 'Submitting transaction to blockchain...'}
          </div>
        )}
      </div>

      {/* Transaction Hash */}
      {txHash && (
        <div className="mt-2 small">
          <span className="text-secondary">Transaction:</span>
          {explorerUrl ? (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-info ms-2 text-break"
            >
              {txHash.slice(0, 10)}...{txHash.slice(-8)}
              <i className="bi bi-box-arrow-up-right ms-1"></i>
            </a>
          ) : (
            <code className="text-info ms-2 text-break">{txHash}</code>
          )}
        </div>
      )}
    </div>
  );
};

export default BlockActionStepper;




